import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Tooltip, Box, Typography, Grid  } from '@mui/material';
import { FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import InfoIcon from '@mui/icons-material/Info';
import InfoBox from '../../components/InfoBox/InfoBox';

import useStyles from './styles';
import { getUserAdoptionData } from '../../redux/Actions/userAdoption.action';


const LoginMetrics = () => {
  const classes = useStyles();
  const [activeClientForLogin, setActiveClientForLogin] = useState('All');
  const [clients, setClients] = useState([]);
  const { allClients } = useSelector((state) => state.instance);
  const [userAdoptionData, setUserAdoptionData] = useState([]);
  const [filteredUserRows, setFilteredUserRows] = useState([]);

  const [filterType, setFilterType] = useState("never");

  const toggleFilter = (type) => {
    setFilterType(type);
  };
  const formatDate = (isoString) => format(new Date(isoString), 'dd/MM/yyyy');


  const customerColumns = [
    {
      field: 'name',
      headerName: 'Customer Name',
      flex: 1,
      headerAlign:'center',
      align:'center',
      sortable: false,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      sortable: false,
      headerAlign:'center',
      align:'center'
    },
    {
      field: 'date',
      headerName: 'Created Date',
      flex: 1,
      sortable: false,
      headerAlign:'center',
      align:'center',
      valueFormatter: (params) => formatDate(params.value)
    }
  ];

  const [userAdoptionCounts, setUserAdoptionCounts] = useState({
    activeUsers: 0,
    monthlyActiveUsers: 0,
    todayActiveUsers: 0,
    recentlyOnline: 0,
    neverCount: 0,
    churnedCount: 0,
  });
  
  const handleClientChangeForLogin = useCallback((event) => {
    setActiveClientForLogin(event.target.value);
  }, [setActiveClientForLogin]);

  useEffect(() => {
  const query = {};
  setUserAdoptionData([]);

  if (activeClientForLogin !== 'All') {
    query.tenantId = activeClientForLogin;
  }

  getUserAdoptionData(query).then((res) => {
    if (!res || res.length === 0) {
      setUserAdoptionCounts({
        tenantId: activeClientForLogin,
        activeUsers: 0,
        churnedCount: 0,
        neverCount: 0,
        monthlyActiveUsers: 0,
        todayActiveUsers: 0,
        recentlyOnline: 0
      });
      setUserAdoptionData([]);
      setFilteredUserRows([]);  
      return;
    }
    let userAdoptionResults = [];

    if (activeClientForLogin === 'All') {
      const summaryCounts = {
        tenantId: 'All',
        activeUsers: 0,
        churnedCount: 0,
        neverCount: 0,
        monthlyActiveUsers: 0,
        todayActiveUsers: 0,
        recentlyOnline: 0
      };

      res.forEach((tenant) => {
        summaryCounts.activeUsers += tenant.activeUsers || 0;
        summaryCounts.monthlyActiveUsers += tenant.monthlyActiveUsers || 0;
        summaryCounts.todayActiveUsers += tenant.todayActiveUsers || 0;
        summaryCounts.recentlyOnline += tenant.recentlyOnline || 0;
        summaryCounts.neverCount += (tenant.neverLoggedInUsers || []).length;
        summaryCounts.churnedCount += (tenant.churnedUsers || []).length;
      });

      userAdoptionResults = res;
      setUserAdoptionCounts(summaryCounts);
    } else {
      const tenant = res[0] || {};
      const summaryCounts = {
        tenantId: tenant.tenantId,
        activeUsers: tenant.activeUsers || 0,
        churnedCount: (tenant.churnedUsers || []).length,
        neverCount: (tenant.neverLoggedInUsers || []).length,
        monthlyActiveUsers: tenant.monthlyActiveUsers || 0,
        todayActiveUsers: tenant.todayActiveUsers || 0,
        recentlyOnline: tenant.recentlyOnline || 0
      };

      userAdoptionResults = res;
      setUserAdoptionCounts(summaryCounts);
    }

    setUserAdoptionData(userAdoptionResults);
  });
}, [activeClientForLogin]);

useEffect(() => {
  if (!userAdoptionData || userAdoptionData.length === 0) return;

  const filteredRows = [];

    const getUsersFrom = () => {
  const pushUsers = (source) => {
    if (filterType === 'never') {
      filteredRows.push(...(source.neverLoggedInUsers || []));
    } else if (filterType === 'churned') {
      filteredRows.push(...(source.churnedUsers || []));
    }
  };

  if (activeClientForLogin === 'All') {
    userAdoptionData.forEach(pushUsers);
  } else {
    pushUsers(userAdoptionData[0] || {});
  }
};

  getUsersFrom();
  setFilteredUserRows(filteredRows);
}, [filterType, userAdoptionData, activeClientForLogin]);


  useEffect(() => {
    if (allClients?.length && allClients[0]?.clients) {
        setClients(allClients[0].clients);
    }
}, [allClients]);

  return (
    <div className={classes.tabViewContainer}>
    <div className={`${classes.tabHeadingContainer} ${classes.containerMargin}`}>
        <p className={classes.heading} >Overall and Per account admin panel adoption insights</p>
        <div className={classes.formGroup}>
            <FormControl variant="filled" className={classes.selectFromControl}>
                <InputLabel id="client-select-label" className={classes.selectLabel}>
                    Client
                </InputLabel>
                <Select
                    labelId="client-select-label"
                    id="client-select-box"
                    value={activeClientForLogin}
                    onChange={handleClientChangeForLogin}
                    label="Select an Instance"
                    className={classes.inputLabel}
                    displayEmpty
                    inputProps={{
                        'aria-label': 'Without label',
                        classes: {
                            icon: classes.selectIcon
                        }
                    }}
                    disableUnderline
                >
                    <MenuItem value="All">All</MenuItem>
                    {
                        clients.map((item) => (
                            <MenuItem value={item.tenantId} key={item.tenantId}>
                                {item.name}
                            </MenuItem>
                        ))
                    }
                </Select>
            </FormControl>
        </div>
    </div>
    
<Grid container spacing={1} className={classes.bottomMargin}>
    <Grid item xs={12} sm={6} md={3}>
        <InfoBox title="Active Users" value={userAdoptionCounts.activeUsers} tooltip="The number of users who have logged in within the past 15 days"/>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
        <InfoBox title="Recently Online" value={userAdoptionCounts.recentlyOnline} tooltip="Users who were online 2 hours before"/>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
        <InfoBox title="Today Active Users" value={userAdoptionCounts.todayActiveUsers} tooltip="The number of users who have logged in today"/>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
        <InfoBox title="Monthly Active Users" value={userAdoptionCounts.monthlyActiveUsers} tooltip="The number of users who have logged in at least once in the past six months"/>
    </Grid>
</Grid>
<div className={`${classes.tabHeadingContainer} ${classes.containerMargin}`}>
    <p className={classes.heading} >Users Who Never Logged In and Churned Customers</p>
</div>
<Grid container spacing={4} className={classes.bottomMargin}>
    <Grid item xs={12} sm={6} md={4}>
        <Box
        onClick={() => toggleFilter("never")}
        className={classes.churnedUserContaier}
        
        sx={{
          border: filterType === 'never' ? '3px solid #3FD1DF' : '3px solid #dedede',
        }}
        >
            <Box className={classes.churnedUserRow}>
                <Box
                
                className={classes.churnedUserTitle}>
                Users Never Logged-In
                </Box>
                <Tooltip
                    placement="right"
                    title="Users who have registered but have not yet accessed the platform"
                >
                    <InfoIcon
                        className={classes.infoIcon}
                        fontSize="small"
                    />
                </Tooltip>
            </Box>
            <Typography variant="span" className={classes.churnedUserCount}>
            {userAdoptionCounts.neverCount}
            </Typography>
        </Box>
    </Grid>
    <Grid item xs={12} sm={6} md={4}>
        <Box
          onClick={() => toggleFilter("churned")}
          className={classes.churnedUserContaier}
            sx={{
              border: filterType === 'churned' ? '3px solid #3FD1DF' : '3px solid #dedede',
            }}>
            <Box className={classes.churnedUserRow}>
                <Box className={classes.churnedUserTitle}
                >
                Churned Users
                </Box>
                <Tooltip
                    placement="right"
                    title="Clients who have not used the services for the past three months following their last engagement"
                >
                    <InfoIcon className={classes.infoIcon}
                        fontSize="small"
                    />
                </Tooltip>
            </Box>
            <Typography variant="span" className={classes.churnedUserCount}>
            {userAdoptionCounts.churnedCount}
            </Typography>
        </Box>
    </Grid>
</Grid>



    <div className={classes.dataTable}>
        <DataGrid
            rows={filteredUserRows}
            columns={customerColumns}
            className={classes.table}
            disableRowSelectionOnClick
            sx={{
            maxHeight: '100%',
            overflowX: 'auto',

            '.MuiDataGrid-selectedRowCount': {
                visibility: 'hidden',
            },
            '.MuiDataGrid-virtualScrollerContent': {
              marginTop: '0px !important'
            }
            }}
            getRowId={(row) => row._id}
            hideFooter
            disableColumnMenu
        />
    </div>
</div>
  )
}

export default LoginMetrics
